import { Grid } from '@mui/material';
import { Field, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useMutation } from 'react-query';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroRichText,
} from '..';
import { useApi } from '../../context';
import { ContactBorrowerModel } from '../../models';
import { FormikUtils, HtmlUtils } from '../../utils';
import { CerebroApiEndpoints } from '../../utils/api';
import { getApiResponseData } from '../../utils/axios';

interface ContactBorrowerFormProps {
	dataRoomGuid: string | undefined;
	bankContactGuid: string | undefined;
	open: boolean;
	close: () => void;
}

export const ContactBorrowerForm: React.FC<ContactBorrowerFormProps> = ({
	dataRoomGuid,
	bankContactGuid,
	open,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const ref = React.useRef<HTMLDivElement>(null);

	const sendMutation = useMutation(
		async ({ values }: ContactBorrowerFormMutationVariables) => {
			const { data, error } = await getApiResponseData<void>(
				api.post(
					CerebroApiEndpoints.Milestones.DirectConnect.ContactBorrower(dataRoomGuid, bankContactGuid),
					values
				)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Email was sent.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={open} onClose={() => close()} PaperProps={{ sx: { width: '700px' } }}>
			<CerebroHeading>Contact Borrower</CerebroHeading>

			<Formik
				initialValues={{ subject: '', emailBody: '' } as ContactBorrowerModel}
				onSubmit={FormikUtils.MutateOnSubmit(sendMutation.mutate)}
			>
				{({ isSubmitting, handleSubmit, values, setFieldValue }) => (
					<CerebroForm
						flatIndicator
						onSubmit={handleSubmit}
						sx={{ marginTop: 3, paddingRight: 3 }}
						scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
					>
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={11}>
								<CerebroLabel id="subject">Subject</CerebroLabel>
								<Field id="subject" as={CerebroInput} name="subject" disabled={isSubmitting} />
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="emailBody">Email Body</CerebroLabel>
								<CerebroRichText
									value={values.emailBody ?? ''}
									onChange={updatedValue => setFieldValue('emailBody', updatedValue)}
									showMinimumToolbar
								/>
							</Grid>

							<Grid item xs={8}>
								<CerebroPrimaryButton loading={isSubmitting} type="submit">
									Send Email
								</CerebroPrimaryButton>
							</Grid>
						</Grid>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type ContactBorrowerFormMutationVariables = FormikUtils.MutateOnSubmitVariables<ContactBorrowerModel>;
